import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';

import { MidotUserDTO, OrganizationDTO, useDeleteUser, UserOrganizationAssignmentDTO } from '@/api/generated';
import EllipsisHorizontalIcon from '@/assets/icons/ellipsis-horizontal.svg?react';
import PencilIcon from '@/assets/icons/pencil.svg?react';
import TrashBinIcon from '@/assets/icons/trash-bin.svg?react';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import BaseCell from '@/components/DataGrid/BaseCell';
import FormattedDatetimeCell from '@/components/DataGrid/FormattedDatetimeCell';
import TextCell from '@/components/DataGrid/TextCell';
import UserCell from '@/components/DataGrid/UserCell';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import LocationAccessDisplay from '@/components/fields/Location/LocationAccessDisplay';
import OfficeDisplay from '@/containers/Organizations/OfficeDisplay';
import StatusDisplay from '@/containers/Organizations/StatusDisplay';
import { SETTINGS_STEP_KEYS, toUserSettings } from '@/root/linker';

import RoleDisplay from './RoleDisplay';

const clientUserColumnHelper = createColumnHelper<UserOrganizationAssignmentDTO>();

export const clientUserColumns = (t: TFunction, canDeleteOnboardingUser?: boolean) =>
  [
    clientUserColumnHelper.display({
      id: 'name',
      header: () => t('users.columns.name'),
      cell: ({ row }) => <UserCell {...row.original} />
    }),
    clientUserColumnHelper.accessor('status', {
      header: () => t('users.columns.status'),
      cell: ({ getValue }) => (
        <BaseCell>
          <StatusDisplay value={getValue()!} />
        </BaseCell>
      )
    }),
    clientUserColumnHelper.display({
      id: 'organization',
      header: () => t('users.columns.organization'),
      cell: ({ row }) => <TextCell>{row.original.organization?.name}</TextCell>
    }),

    clientUserColumnHelper.display({
      id: 'access',
      header: () => t('users.columns.access'),
      cell: ({ row }) => (
        <BaseCell>
          <LocationAccessDisplay
            organization={row.original.organization as OrganizationDTO}
            assignments={row.original.locationRoleAssignmentsSummary}
            totalCount={row.original.locationRoleAssignmentsCount}
          />
        </BaseCell>
      )
    }),
    clientUserColumnHelper.accessor('email', {
      header: () => t('users.columns.email'),
      cell: ({ getValue }) => <TextCell>{getValue() ?? ''}</TextCell>
    }),
    clientUserColumnHelper.accessor('phone', {
      header: () => t('users.columns.phone'),
      cell: ({ getValue }) => <TextCell>{getValue() ?? ''}</TextCell>
    }),
    clientUserColumnHelper.accessor('jobTitle', {
      header: () => t('users.columns.title'),
      cell: ({ getValue }) => <TextCell>{getValue() ?? ''}</TextCell>
    }),
    clientUserColumnHelper.accessor('createdBy', {
      header: () => t('users.columns.createdBy'),
      cell: ({ getValue }) => {
        const createdBy = getValue();

        return <UserCell {...createdBy} />;
      }
    }),
    clientUserColumnHelper.accessor('createdAt', {
      header: () => t('users.columns.createdDate'),
      cell: ({ getValue }) => <FormattedDatetimeCell value={getValue()} variant="datetime" />
    }),
    clientUserColumnHelper.accessor('lastLoginAt', {
      header: () => t('users.columns.lastLogin'),
      cell: ({ getValue }) => <FormattedDatetimeCell value={getValue()} variant="datetime" />
    }),
    clientUserColumnHelper.display({
      id: 'actions',
      header: () => t('users.columns.actions'),
      cell: ({ row }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const navigate = useNavigate();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const queryClient = useQueryClient();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { mutateAsync: deleteUser, isLoading: isDeleting } = useDeleteUser();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleEdit = useCallback(
          () =>
            navigate(
              toUserSettings({
                userId: row.original.userId!,
                stepKey: SETTINGS_STEP_KEYS.BASIC_INFO
              })
            ),
          [navigate, row.original.userId]
        );

        const shouldShowDeleteAction = canDeleteOnboardingUser && row.original.status === 'ONBOARDING';

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteClick = useCallback(() => setIsDeleteConfirmationOpen(true), []);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteConfirm = useCallback(async () => {
          await deleteUser({ userId: row.original.userId! });
          setIsDeleteConfirmationOpen(false);
          queryClient.invalidateQueries(['/v1/user-organization-assignments']);
        }, [deleteUser, queryClient, row.original.userId]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteCancel = useCallback(() => setIsDeleteConfirmationOpen(false), []);

        return (
          <BaseCell>
            <DropdownMenu
              customIcon={<EllipsisHorizontalIcon />}
              menuProps={{
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                }
              }}
              autoClose
            >
              <DropdownMenuItem icon={<PencilIcon />} text="Edit" onClick={handleEdit} disabled={false} />
              {shouldShowDeleteAction && (
                <DropdownMenuItem
                  icon={
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', width: 24, height: 24, '& svg': { width: '100%', height: '100%' } }}
                    >
                      <TrashBinIcon />
                    </Box>
                  }
                  text={t('users.actions.delete')}
                  onClick={handleDeleteClick}
                  disabled={false}
                  sx={{ color: 'error.main' }}
                />
              )}
            </DropdownMenu>
            {shouldShowDeleteAction && (
              <ConfirmationModal
                open={isDeleteConfirmationOpen}
                onConfirm={handleDeleteConfirm}
                onDiscard={handleDeleteCancel}
                onClose={handleDeleteCancel}
                title={t('users.deleteUser.confirmation')}
                confirmButtonLabel={t('users.deleteUser.confirm')}
                confirmButtonStartIcon={<TrashBinIcon />}
                discardButtonLabel={t('common:cancel')}
                isLoading={isDeleting}
                loadingLabel={t('users.deleteUser.deleting')}
              />
            )}
          </BaseCell>
        );
      }
    })
  ] as ColumnDef<UserOrganizationAssignmentDTO>[];

const midotUserColumnHelper = createColumnHelper<MidotUserDTO>();

export const midotUserColumns = (t: TFunction, canDeleteOnboardingUser?: boolean) =>
  [
    midotUserColumnHelper.display({
      id: 'name',
      header: () => t('users.columns.name'),
      cell: ({ row }) => <UserCell {...row.original} />
    }),
    midotUserColumnHelper.accessor('status', {
      header: () => t('users.columns.status'),
      cell: ({ getValue }) => (
        <BaseCell>
          <StatusDisplay value={getValue()!} />
        </BaseCell>
      )
    }),
    midotUserColumnHelper.accessor('email', {
      header: () => t('users.columns.email'),
      cell: ({ getValue }) => <TextCell>{getValue() ?? ''}</TextCell>
    }),
    midotUserColumnHelper.accessor('phone', {
      header: () => t('users.columns.phone'),
      cell: ({ getValue }) => <TextCell>{getValue() ?? ''}</TextCell>
    }),
    midotUserColumnHelper.accessor('title', {
      header: () => t('users.columns.title'),
      cell: ({ getValue }) => <TextCell>{getValue() ?? ''}</TextCell>
    }),
    midotUserColumnHelper.accessor('offices', {
      header: () => t('users.columns.offices'),
      cell: ({ getValue }) => (
        <BaseCell>
          <OfficeDisplay value={getValue()!} />
        </BaseCell>
      )
    }),
    midotUserColumnHelper.accessor('roleAssignmentsSummary', {
      header: () => t('users.columns.roles'),
      cell: ({ getValue }) => (
        <BaseCell>
          <RoleDisplay value={getValue()!} />
        </BaseCell>
      )
    }),
    midotUserColumnHelper.accessor('createdBy', {
      header: () => t('users.columns.createdBy'),
      cell: ({ getValue }) => {
        const createdBy = getValue();

        return <UserCell {...createdBy} />;
      }
    }),
    midotUserColumnHelper.accessor('createdAt', {
      header: () => t('users.columns.createdDate'),
      cell: ({ getValue }) => <FormattedDatetimeCell value={getValue()} variant="datetime" />
    }),
    midotUserColumnHelper.accessor('lastLoginAt', {
      header: () => t('users.columns.lastLogin'),
      cell: ({ getValue }) => <FormattedDatetimeCell value={getValue()} variant="datetime" />
    }),
    midotUserColumnHelper.display({
      id: 'actions',
      header: () => t('users.columns.actions'),
      cell: ({ row }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const navigate = useNavigate();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const queryClient = useQueryClient();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { mutateAsync: deleteUser, isLoading: isDeleting } = useDeleteUser();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleEdit = useCallback(
          () =>
            navigate(
              toUserSettings({
                userId: row.original.id!,
                stepKey: SETTINGS_STEP_KEYS.BASIC_INFO
              })
            ),
          [navigate, row.original.id]
        );

        const shouldShowDeleteAction = canDeleteOnboardingUser && row.original.status === 'ONBOARDING';

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteClick = useCallback(() => setIsDeleteConfirmationOpen(true), []);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteConfirm = useCallback(async () => {
          await deleteUser({ userId: row.original.id! });
          setIsDeleteConfirmationOpen(false);
          queryClient.invalidateQueries(['/v1/midot/users']);
        }, [deleteUser, queryClient, row.original.id]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteCancel = useCallback(() => setIsDeleteConfirmationOpen(false), []);

        return (
          <BaseCell>
            <DropdownMenu
              customIcon={<EllipsisHorizontalIcon />}
              menuProps={{
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                }
              }}
              autoClose
            >
              <DropdownMenuItem icon={<PencilIcon />} text="Edit" onClick={handleEdit} disabled={false} />
              {shouldShowDeleteAction && (
                <DropdownMenuItem
                  icon={
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', width: 24, height: 24, '& svg': { width: '100%', height: '100%' } }}
                    >
                      <TrashBinIcon />
                    </Box>
                  }
                  text={t('users.actions.delete')}
                  onClick={handleDeleteClick}
                  disabled={false}
                  sx={{ color: 'error.main' }}
                />
              )}
            </DropdownMenu>
            {shouldShowDeleteAction && (
              <ConfirmationModal
                open={isDeleteConfirmationOpen}
                onConfirm={handleDeleteConfirm}
                onDiscard={handleDeleteCancel}
                onClose={handleDeleteCancel}
                title={t('users.deleteUser.confirmation')}
                confirmButtonLabel={t('users.deleteUser.confirm')}
                confirmButtonStartIcon={<TrashBinIcon />}
                discardButtonLabel={t('common:cancel')}
                isLoading={isDeleting}
                loadingLabel={t('users.deleteUser.deleting')}
              />
            )}
          </BaseCell>
        );
      }
    })
  ] as ColumnDef<MidotUserDTO>[];
